import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { Add, AppIconHelp, ChevronRight, Clock, Close, Launch } from "@gaming/xds-react-js-icons";

import { getAssistantStageContent } from "../../constants/supportAssistantContent";
import { partnerSupportResourceUrls } from "../../constants/urls";
import { FeatureFlags, useFeatureFlag } from "../../featureFlags";
import { useSupportAssistant } from "../../helpers/supportAssistantStore";
import {
  formatSupportDate,
  getSupportCardState,
  getSupportCasePath,
  SupportCardState,
  supportNewCasePath,
  supportPagePath,
} from "../../helpers/supportCases";
import { useSupportCases } from "../../helpers/useSupportCases";
import { useStrings } from "../../localization/localizationHelpers";
import { PageLoadingSpinner } from "../PageLoadingSpinner";
import { AssistantChat, AssistantHome } from "../SupportAssistant";
import * as styles from "./SupportHelpButton.module.css";

const helpSlotId = "uhfHelpSlot";
const flyoutTitleId = "support-flyout-title";
const recentCaseCount = 3;
// Each page mounts its own layout, so open state is stored to survive navigation.
const openStorageKey = "hubSupportFlyoutOpen";

const readIsOpen = () => {
  try {
    return sessionStorage.getItem(openStorageKey) === "true";
  } catch {
    return false;
  }
};

/** Tracks the mocked UHF's help slot, which can be re-rendered when the header changes. */
const useHelpSlot = () => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const update = () => setSlot(document.getElementById(helpSlotId));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return slot;
};

export const SupportHelpButton: React.FC = () => {
  const slot = useHelpSlot();
  const [isRestored, setIsRestored] = useState(readIsOpen);
  const [isOpen, setIsOpen] = useState(isRestored);
  const [isClosing, setIsClosing] = useState(false);
  const isCasesEnabled = useFeatureFlag(FeatureFlags.EnableHubSupportCases);
  const isAssistantEnabled = useFeatureFlag(FeatureFlags.EnableHubSupportAssistant);
  const { data, errorStatus, isLoading } = useSupportCases(isOpen && isCasesEnabled);
  const assistant = useSupportAssistant().state;
  const isChat = isAssistantEnabled && assistant.view === "chat";
  const stageDocs = getAssistantStageContent(assistant.home?.stage).docs;
  const stageDocStrings = useStrings(
    ...stageDocs.flatMap(doc => [doc.titleKey, doc.descriptionKey])
  );

  useEffect(() => {
    try {
      sessionStorage.setItem(openStorageKey, String(isOpen));
    } catch {
      // Storage can be unavailable; the flyout then just closes on navigation.
    }
  }, [isOpen]);

  const [
    helpLabel,
    closeLabel,
    recentRequests,
    manageRequests,
    submitNewRequest,
    usualReplyTime,
    recommendedDocs,
    validateTitle,
    validateDescription,
    certificationTitle,
    certificationDescription,
    developerDocs,
    exploreDocs,
    exploreDocsDescription,
    environmentTitle,
    environmentDescription,
    shipTitle,
    shipDescription,
    community,
    communityDescription,
    gitHub,
    discord,
    getSupport,
    getSupportDescription,
    submitRequest,
    submittedOn,
    lastUpdatedOn,
    closedOn,
    newReply,
    awaitingResponse,
  ] = useStrings(
    "hubSupportHelpButton",
    "closeAriaLabel",
    "hubSupportRecentRequests",
    "hubSupportManageRequests",
    "hubSupportSubmitNewRequest",
    "hubSupportUsualReplyTime",
    "hubSupportRecommendedDocs",
    "hubSupportDocValidateTitle",
    "hubSupportDocValidateDescription",
    "hubSupportDocCertificationTitle",
    "hubSupportDocCertificationDescription",
    "hubSupportDeveloperDocs",
    "hubSupportExploreDocs",
    "hubSupportExploreDocsDescription",
    "hubSupportDocEnvironmentTitle",
    "hubSupportDocEnvironmentDescription",
    "hubSupportDocShipTitle",
    "hubSupportDocShipDescription",
    "hubSupportCommunity",
    "hubSupportCommunityDescription",
    "hubSupportGitHub",
    "hubSupportDiscord",
    "hubSupportGetSupport",
    "hubSupportGetSupportDescription",
    "hubSupportSubmitRequest",
    "hubSupportSubmittedOn",
    "hubSupportLastUpdatedOn",
    "hubSupportClosedOn",
    "hubSupportNewReply",
    "hubSupportAwaitingResponse"
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsClosing(true);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  if (!slot) {
    return null;
  }

  // Keep the flyout mounted until its exit animation ends.
  const close = () => setIsClosing(true);
  const onFlyoutAnimationEnd = () => {
    if (isClosing) {
      setIsOpen(false);
      setIsClosing(false);
      // Only the navigation-restored open skips the entry animation.
      setIsRestored(false);
    }
  };
  const isExpanded = isOpen && !isClosing;
  const cases = isCasesEnabled ? data?.cases ?? [] : [];
  const hasCases = cases.length > 0;
  // 403 means the caller has no hub access to support cases.
  const canUseSupport = errorStatus !== 403;

  // Flat [title, description, ...] pairs matching docUrls by index.
  const [docStrings, docUrls] = isAssistantEnabled
    ? [stageDocStrings, stageDocs.map(doc => doc.url)]
    : [
        hasCases
          ? [validateTitle, validateDescription, certificationTitle, certificationDescription]
          : [environmentTitle, environmentDescription, shipTitle, shipDescription],
        [partnerSupportResourceUrls.gdkDocs, partnerSupportResourceUrls.devDocs],
      ];

  const renderExternalLink = (label: string, href: string) => (
    <a href={href} target="_blank" rel="noreferrer" className={styles.textLink}>
      {label}
      <Icon aria-hidden="true" icon={Launch} iconSize={IconSize.Custom} />
    </a>
  );

  const renderCaseState = (state: SupportCardState, createdOn: string, modifiedOn: string) => {
    switch (state) {
      case SupportCardState.Submitted:
        return (
          <>
            <span>{submittedOn.replace("{0}", formatSupportDate(createdOn))}</span>
            <span className={styles.replyTime}>
              <Icon aria-hidden="true" icon={Clock} iconSize={IconSize.Custom} />
              {usualReplyTime}
            </span>
          </>
        );
      case SupportCardState.NewReply:
        return <span className={`${styles.dot} ${styles.newReply}`}>{newReply}</span>;
      case SupportCardState.AwaitingPartner:
        return <span className={`${styles.dot} ${styles.awaiting}`}>{awaitingResponse}</span>;
      case SupportCardState.Closed:
        return <span>{closedOn.replace("{0}", formatSupportDate(modifiedOn))}</span>;
      default:
        return <span>{lastUpdatedOn.replace("{0}", formatSupportDate(modifiedOn))}</span>;
    }
  };

  const renderSections = () => (
    <>
      {hasCases && (
        <section className={styles.section}>
          <h3>{recentRequests}</h3>
          <ul className={styles.caseList}>
            {cases.slice(0, recentCaseCount).map(supportCase => {
              const state = getSupportCardState(supportCase);
              return (
                <li key={supportCase.caseId}>
                  <Link
                    to={getSupportCasePath(supportCase.caseId)}
                    className={`${styles.caseCard} ${
                      state === SupportCardState.Closed ? styles.closedCard : ""
                    }`}
                    data-testid="support-flyout-case"
                  >
                    <span className={styles.caseTitle}>{supportCase.title}</span>
                    <span className={styles.caseState}>
                      {renderCaseState(state, supportCase.createdOn, supportCase.modifiedOn)}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className={styles.linkRow}>
            <Link to={supportPagePath} className={styles.textLink}>
              {manageRequests.replace("{0}", String(data?.totalCount ?? cases.length))}
              <Icon aria-hidden="true" icon={ChevronRight} iconSize={IconSize.Custom} />
            </Link>
            <Link to={supportNewCasePath} className={styles.textLink}>
              {submitNewRequest}
              <Icon aria-hidden="true" icon={Add} iconSize={IconSize.Custom} />
            </Link>
          </div>
        </section>
      )}
      <section className={styles.section}>
        <h3>{isAssistantEnabled || hasCases ? recommendedDocs : exploreDocs}</h3>
        {!isAssistantEnabled && !hasCases && <p>{exploreDocsDescription}</p>}
        <ul className={styles.resourceList}>
          {docUrls.map((href, index) => (
            <li key={docStrings[index * 2]}>
              <a href={href} target="_blank" rel="noreferrer" className={styles.resourceCard}>
                <span className={styles.resourceTitle}>{docStrings[index * 2]}</span>
                <span className={styles.resourceDescription}>{docStrings[index * 2 + 1]}</span>
              </a>
            </li>
          ))}
        </ul>
        {renderExternalLink(developerDocs, partnerSupportResourceUrls.devDocs)}
      </section>
      <section className={styles.section}>
        <h3>{community}</h3>
        <p>{communityDescription}</p>
        <div className={styles.linkRow}>
          {renderExternalLink(gitHub, partnerSupportResourceUrls.github)}
          {renderExternalLink(discord, partnerSupportResourceUrls.discord)}
        </div>
      </section>
      {isCasesEnabled && !hasCases && canUseSupport && (
        <section className={styles.section}>
          <h3>{getSupport}</h3>
          <p>{getSupportDescription}</p>
          <Link to={supportNewCasePath} className={styles.textLink}>
            {submitRequest}
            <Icon aria-hidden="true" icon={ChevronRight} iconSize={IconSize.Custom} />
          </Link>
        </section>
      )}
    </>
  );

  return (
    <>
      {createPortal(
        <button
          type="button"
          className={styles.helpButton}
          aria-label={helpLabel}
          aria-expanded={isExpanded}
          onClick={() => {
            if (isExpanded) {
              close();
            } else {
              setIsClosing(false);
              setIsOpen(true);
            }
          }}
          data-testid="support-help-button"
        >
          <Icon aria-hidden="true" icon={AppIconHelp} iconSize={IconSize.Custom} />
        </button>,
        slot
      )}
      {isOpen &&
        createPortal(
          <div
            className={`${styles.flyout} ${isChat ? styles.chatFlyout : ""} ${
              isClosing ? styles.closing : isRestored ? styles.restored : ""
            }`}
            onAnimationEnd={onFlyoutAnimationEnd}
            role="dialog"
            aria-labelledby={flyoutTitleId}
            data-testid="support-flyout"
          >
            <div className={styles.flyoutHeader}>
              <h2 id={flyoutTitleId}>{helpLabel}</h2>
              <button
                type="button"
                className={styles.closeButton}
                aria-label={closeLabel}
                onClick={close}
              >
                <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
              </button>
            </div>
            {isChat ? (
              <div className={styles.chatBody}>
                <AssistantChat />
              </div>
            ) : (
              <div className={styles.flyoutBody}>
                {isAssistantEnabled && <AssistantHome />}
                {isLoading ? <PageLoadingSpinner /> : renderSections()}
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};
